import { useIntl } from "react-intl";
import { useFormContext } from "react-hook-form";
import FormField from "components/molecules/FormField/FormField";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { getContactBook } from "api";
import { Contact } from "api/models";
import testIds from "constants/testIds";

import SearchSelect from "../../Common/SearchSelect";
import { handleContactRenderOption } from "../PickupDeliveryAddress/helpers";

/**
 * Props interface for ContactForm component
 */
interface ContactFormProps {
  partyType: "notify_party" | "consignee";
  contactBook: Contact[];
  disabled: boolean;
  required: boolean;
}

export default function ContactForm({ partyType, contactBook, disabled, required }: ContactFormProps) {
  const intl = useIntl();
  const { getValues, setValue } = useFormContext();
  const selectedContact = useRef(null);

  const contactBookHashMap: any = useMemo(() => {
    const data: any = {};
    if (contactBook?.length) {
      contactBook.forEach((contact: any) => {
        data[contact.name.toLowerCase()] = contact;
      });
    }
    return data;
  }, [contactBook]);

  const filterContactName = (option: any, itemProps: any, filterKey = "label") => {
    if (selectedContact.current) {
      return true;
    }
    return (
      option[filterKey].toLowerCase().indexOf(itemProps.text.toLowerCase()) !== -1 ||
      option.company_name.toLowerCase().indexOf(itemProps.text.toLowerCase()) !== -1
    );
  };

  const autoFillContact = useCallback((event) => {
    const contact = event[0];
    if (contact) {
      setValue(`schedule.${partyType}.contact_name`, contact.label, { shouldValidate: true });
      setValue(`schedule.${partyType}.contact_phone`, contact.phone_number, { shouldValidate: true });
      setValue(`schedule.${partyType}.contact_email`, contact.email, { shouldValidate: true });
    }
  }, []);

  useEffect(() => {
    getContactBook();
  }, []);

  return (
    <div className="flex-row flex-wrap main-wrapper">
      <div className="w-192 dropdown-div">
        <SearchSelect
          name={`schedule.${partyType}.contact_name`}
          testId={
            partyType === "notify_party"
              ? testIds.REQUEST_FORM_SCHEDULE_NOTIFY_PARTY_CONTACT_NAME
              : testIds.REQUEST_FORM_SCHEDULE_CONSIGNEE_CONTACT_NAME
          }
          enableManualValue
          required={required}
          label={intl.formatMessage({
            id: "general.name",
            defaultMessage: "Name",
          })}
          preselect={false}
          onFocus={() => {
            if (contactBookHashMap[getValues(`schedule.${partyType}.contact_name`)?.toLowerCase()]) {
              selectedContact.current = contactBookHashMap[getValues(`schedule.${partyType}.contact_name`)?.toLowerCase()];
            }
          }}
          onInputChange={(val: any) => {
            if (!contactBookHashMap[val?.toLowerCase()]) {
              selectedContact.current = null;
            }
          }}
          options={
            contactBook &&
            contactBook.map((contact: any) => ({
              id: contact.id,
              label: contact.name,
              value: contact.name,
              company_name: contact.company_name,
              phone_number: contact.phone_number,
              email: contact.email,
            }))
          }
          placeholder={intl.formatMessage({
            id: "new-request.schedule.chooseContactPerson",
            defaultMessage: "Search for a contact person",
          })}
          filterBy={filterContactName}
          handleRenderOption={handleContactRenderOption}
          onSelect={autoFillContact}
          disable={disabled}
        />
      </div>

      <div className="w-192">
        <FormField
          id={`schedule.${partyType}.contact_phone`}
          label={intl.formatMessage({
            id: "new-request.schedule.pickupContactPhone",
            defaultMessage: "Phone",
          })}
          type="textinput"
          field_id={`schedule.${partyType}.contact_phone`}
          placeholder={intl.formatMessage({
            id: "new-request.schedule.phoneInfo",
            defaultMessage: "Type phone number here...",
          })}
          mandatory={required}
          onChange={(e) => {
            setValue(`schedule.${partyType}.contact_phone`, e.target.value);
          }}
          disabled={disabled}
          testId={
            partyType === "notify_party"
              ? testIds.REQUEST_FORM_SCHEDULE_NOTIFY_PARTY_CONTACT_PHONE
              : testIds.REQUEST_FORM_SCHEDULE_CONSIGNEE_CONTACT_PHONE
          }
        />
      </div>
      <div className="w-192">
        <FormField
          id={`schedule.${partyType}.contact_email`}
          label={intl.formatMessage({
            id: "new-request.schedule.pickupContactEmail",
            defaultMessage: "Email",
          })}
          placeholder={intl.formatMessage({
            id: "new-request.schedule.emailInfo",
            defaultMessage: "Type email here...",
          })}
          type="textinput"
          field_id={`schedule.${partyType}.contact_email`}
          onChange={(e) => {
            setValue(`schedule.${partyType}.contact_email`, e.target.value);
          }}
          mandatory={required}
          disabled={disabled}
          testId={
            partyType === "notify_party"
              ? testIds.REQUEST_FORM_SCHEDULE_NOTIFY_PARTY_CONTACT_EMAIL
              : testIds.REQUEST_FORM_SCHEDULE_CONSIGNEE_CONTACT_EMAIL
          }
        />
      </div>
    </div>
  );
}
